/**
 * This exposes functions that will return true when a feature toggle has been set.
 * It will work both on the server and client
 *
 * To enable a feature:
 *
 *  1) Find the feature name in the `Feature` type below, for instance 'MYACCOUNT'
 *  2) Set an env var using the FEATURE_ prefix when starting the server, ie FEATURE_MYACCOUNT
 *  3) Run the app with `FEATURE_MYACCOUNT=true yarn serve` in dev mode or you can add it the .env file
 *
 * To add a feature toggle
 *
 *  1) Add a name to the Feature type, AWESOME_SAUCE for example,
 *      ex: `| 'AWESOME_SAUCE'`
 *  2) Export a function named isAwesomeSauceEnabled
 *      ex: `export const isAwesomeSauceEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'AWESOME_SAUCE')`
 *  3) To enable it see above
 */

import getConfig from 'next/config'

type Feature =
  | 'AAB'
  | 'ABTESTING'
  | 'ADDRESSBOOK'
  | 'MYACCOUNT'
  | 'CONTACTLENS'
  | 'CONTACTLENS_STOCK'
  | 'CROSS_SELL'
  | 'REVIEWS'
  | 'SOLUTIONS'
  | 'SEARCH'
  | 'GPS_STORES'
  | 'STORES_PLACE_PREDICTIONS'
  | 'MULTILINGUAL'
  | 'VTO'
  | 'PRESCRIPTION_GLASSES'
  | 'PRESCRIPTION_GLASSES_CONFIGURATOR_VALIDATION_STEP'
  | 'PRESCRIPTION_FILE'
  | 'STORE_SEARCH'
  | 'SUBSCRIPTION_HAS_ONE_ADDRESS'
  | 'SUBSCRIPTION_UPDATE_ADDRESS'

type FeatureCheck = () => boolean

let enabledFeatures: string[] | undefined

const getEnabledFeatures = () => {
  if (!enabledFeatures) {
    enabledFeatures = getConfig().publicRuntimeConfig.grandvision.features as string[]

    if (typeof window === 'undefined') {
      console.info(`> Feature toggles enabled: ${enabledFeatures.length ? enabledFeatures : '-'}`)
    }
  }
  return enabledFeatures
}

const isFeatureEnabled = (feature: Feature): boolean =>
  getEnabledFeatures().some((e) => e.toUpperCase() === feature.toUpperCase())

export const isAABEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'AAB')
export const isABTestingEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'ABTESTING')
export const isAddressBookEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'ADDRESSBOOK')
export const isMyAccountEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'MYACCOUNT')
export const isContactLensEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'CONTACTLENS')
export const isContactLensStockEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'CONTACTLENS_STOCK'
)
export const isCrossSellEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'CROSS_SELL')
export const isReviewsEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'REVIEWS')
export const isSolutionsEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'SOLUTIONS')
export const isSearchEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'SEARCH')
export const isVtoEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'VTO')
export const isGPSEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'GPS_STORES')
export const isPlacePredictionsEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'STORES_PLACE_PREDICTIONS'
)
export const isMultilingualEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'MULTILINGUAL')
export const isPrescriptionGlassesEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'PRESCRIPTION_GLASSES'
)
export const isPrescriptionGlassesConfiguratorValidationStepEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'PRESCRIPTION_GLASSES_CONFIGURATOR_VALIDATION_STEP'
)
export const isPrescriptionFileEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'PRESCRIPTION_FILE'
)
export const isStoreSearchEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'STORE_SEARCH')
export const isSubscriptionHasOneAddressEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'SUBSCRIPTION_HAS_ONE_ADDRESS'
)
export const isSubscriptionUpdateAddressEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'SUBSCRIPTION_UPDATE_ADDRESS'
)
